/**
 * App.js
 *
 * Main App component for WizeAssist:
 * - Provides global layout and navigation
 * - Implements React Router for page routing
 * - Incorporates Framer Motion for smooth page transitions
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
// import { Analytics } from '@vercel/analytics/react';  // Import Vercel Analytics

// Pages/Components
import HomePage from './components/HomePage';
import AboutUs from './components/AboutUs';
import SolutionsPage from './components/SolutionsPage';
import Marketplace from './components/Marketplace';
import Blog from './components/Blog';
import ContactUs from './components/ContactUs';
import Pricing from './components/Pricing';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import NotFound from './components/NotFound'; // For 404
import Dashboard from './components/Dashboard'; // Import the Dashboard component
import NavigationBar from './components/Navbar'; // Import the Navbar component

// Styles
import './styles/App.css'; // Custom global/app-level CSS

// --- Redirect components for login pages --- //
function Login() {
  React.useEffect(() => {
    window.location.href = 'https://frontend-899566549579.europe-west1.run.app/login';
  }, []);
  return (
    <div>
      Redirecting to WizeAssist login...
      <br />
      <a href="https://frontend-899566549579.europe-west1.run.app/login">
        Click here if you are not redirected.
      </a>
    </div>
  );
}

/**
 * PageWrap:
 * A reusable wrapper to add Framer Motion animations
 * for route transitions, giving a polished look.
 */
function PageWrap({ children }) {
  const location = useLocation();

  return (
    <motion.div
      key={location.pathname}
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="page-wrap"
    >
      {children}
    </motion.div>
  );
}

/**
 * RoutesWithAnimation:
 * Handles routing with page transitions using Framer Motion.
 */
function RoutesWithAnimation() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes key={location.pathname} location={location}>
        <Route path="/" element={<PageWrap><HomePage /></PageWrap>} />
        <Route path="/about-us" element={<PageWrap><AboutUs /></PageWrap>} />
        <Route path="/solutions" element={<PageWrap><SolutionsPage /></PageWrap>} />
        <Route path="/marketplace" element={<PageWrap><Marketplace /></PageWrap>} />
        <Route path="/blog" element={<PageWrap><Blog /></PageWrap>} />
        <Route path="/contact" element={<PageWrap><ContactUs /></PageWrap>} />
        <Route path="/pricing" element={<PageWrap><Pricing /></PageWrap>} />

        {/* --- Login Redirects --- */}
        <Route path="/login" element={<PageWrap><Login /></PageWrap>} />

        {/* Manager/Player Routes → Dash placeholder pages */}
        <Route path="/manager" element={<PageWrap><Dashboard /></PageWrap>} />

        <Route path="/PrivacyPolicy" element={<PageWrap><PrivacyPolicy /></PageWrap>} />
        <Route path="/terms" element={<PageWrap><TermsOfService /></PageWrap>} />
        {/* 404 Not Found Route */}
        <Route path="*" element={<PageWrap><NotFound /></PageWrap>} />
      </Routes>
    </AnimatePresence>
  );
}

/**
 * Footer:
 * A simple footer component.
 */
function Footer() {
  return (
    <footer className="footer bg-dark text-white py-4">
      <div className="container text-center">
        <div className="mb-3">
          <NavLink to="/PrivacyPolicy" className="text-white me-3">
            Privacy Policy
          </NavLink>
          <NavLink to="/terms" className="text-white me-3">
            Terms of Service
          </NavLink>
          <NavLink to="/contact" className="text-white">
            Contact Us
          </NavLink>
        </div>
        <p className="mb-0">&copy; {new Date().getFullYear()} WizeAssist. All Rights Reserved.</p>
      </div>
    </footer>
  );
}

function App() {
  return (
    <Router>
      <div className="app-container d-flex flex-column min-vh-100">
        {/* Navigation Bar */}
        <NavigationBar />

        {/* Page Content with Animations */}
        <RoutesWithAnimation />

        {/* Footer */}
        <Footer />
      </div>
    </Router>
  );
}

export default App;
